// @ts-strict-ignore
import {Component, inject, Injector, OnInit, signal} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {CreateDraftEventRequest, EventColors, EventType, FetchDataType, Modules} from "@models";
import {SimConfigurationService, SimEventService, SimUserExtendedService, SimUtilityService} from "@services";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Translations} from "@translations";
import {EnumLabel} from "../../../../../shared/shared-pipes/enum-label.pipe";
import {Router} from "@angular/router";

export interface DialogData_CreateDraftEventDialogInput extends MatDialogDataBaseInput {
	preselectedDate: Date;
}

export interface DialogData_SelectEventTypeDialogOutput extends MatDialogDataBaseOutput {
	eventId: string;
}

@Component({
	selector: "sim-create-draft-event-dialog",
	templateUrl: "./create-draft-event-dialog.component.html",
	styleUrls: ["./create-draft-event-dialog.component.scss"],
	standalone: false,
})
export class CreateDraftEventDialogComponent
	extends BasicMaterialDialog<
		CreateDraftEventDialogComponent,
		DialogData_CreateDraftEventDialogInput,
		DialogData_SelectEventTypeDialogOutput
	>
	implements OnInit
{
	// Properties
	public promise = signal<Promise<unknown> | null>(null);

	// Readonly
	public readonly T = Translations;
	protected readonly hasLicenseModule = SimConfigurationService.hasLicenseModule;
	protected readonly FetchDataType = FetchDataType;
	protected readonly EventType = EventType;
	protected readonly Modules = Modules;
	protected readonly EventColors = EventColors;
	protected readonly EnumLabel = EnumLabel;
	protected readonly AllActiveEventTypes: EventType[] = Object.keys(EventType)
		.filter((key) => !isNaN(Number(key)))
		.map((key) => Number(key))
		.filter((key) => {
			switch (key) {
				case EventType.Exam:
					return this.hasLicenseModule(Modules.Exam);
				case EventType.PeerPractice:
					return this.hasLicenseModule(Modules.PeerPractice);
				case EventType.MultiRoomRecording:
					return this.hasLicenseModule(Modules.MultiRoomRecording);
				case EventType.AutomaticRecording:
					return this.hasLicenseModule(Modules.AutomaticRecording);
				case EventType.Simulation:
				default:
					return true;
			}
		});

	// Injections
	private _eventService = inject(SimEventService);
	private _simUserService = inject(SimUserExtendedService);
	private _router: Router = inject(Router);

	// Form
	public form: FormGroup<{
		name: FormControl<string>;
		type: FormControl<EventType>;
		start: FormControl<Date>;
		end: FormControl<Date>;
	}>;

	constructor(injector: Injector) {
		super(injector);
	}

	public ngOnInit() {
		this.form = new FormGroup({
			name: new FormControl<string>("", [Validators.required]),
			type: new FormControl<EventType>(EventType.Simulation, [Validators.required]),
			start: new FormControl<Date>(this.data.preselectedDate, [Validators.required]),
			end: new FormControl<Date>(this.data.preselectedDate, [Validators.required]),
		});
	}

	protected async doSubmit(): Promise<void> {
		// Validate
		if (this.form.invalid || !!this.promise()) {
			return;
		}

		// Send request
		const request: CreateDraftEventRequest = {
			eventType: this.form.value.type,
			eventName: this.form.value.name,
			start: SimUtilityService.getStartofDay(this.form.value.start).toDate(),
			end: SimUtilityService.getEndofDay(this.form.value.end).toDate(),
			ownerUserId: this._simUserService.user.id,
		};
		const promise = this._eventService.createDraftEvent(request);
		this.promise.set(promise);
		const result = await promise;
		await this._router.navigate([`/web-manager/list/event-manager/${result.event.id}`]);
	}
}
